import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { ReservationStatusCode } from 'app/event/models/reservation-status-code.enum';
import { ReservationCompareService } from './reservation-compare-http-service';
import { ReservationCompare } from '../models/reservation-compare';
import { ReservationUtils } from '../utils/reservation-utils';
import { MatchTypeEnum } from '../models/match-type.enum';
import { catchError, map, take } from 'rxjs/operators';
import { OVERVIEW_STATUS_CODES, ReservationCompareStatusCode, STATUS_CODE_RSVP } from '../models/reservation-status-code.enum';
import { StaySummary } from '../models/stay-summary';
import { isNullOrUndefined } from 'util';
import { SummaryRecordTypeEnum } from '../models/summary-record-type.enum';
import { LogService } from '../../shared/log/log.service';

@Injectable()
export class ReservationCompareStore {

  public static readonly STATUS_CODES = [
    STATUS_CODE_RSVP,
    ReservationStatusCode.RESERVED,
    ReservationStatusCode.NO_SHOW,
    ReservationStatusCode.CANCELLED,
    ReservationStatusCode.CHECKED_OUT,
    ReservationStatusCode.CHECKED_IN ];

  private currentReservationList: BehaviorSubject<List<ReservationCompare>> =
    new BehaviorSubject(List([]));

  private currentActiveStatusCodes: BehaviorSubject<Array<ReservationCompareStatusCode>> =
    new BehaviorSubject(ReservationCompareStore.STATUS_CODES);

  private currentReservationSummaryState: BehaviorSubject<boolean> =
    new BehaviorSubject(true);

  public selectedRsvpId: number = null;
  public selectedReservationId: number = null;

  constructor(private flashMessagesService: FlashMessagesService,
              private reservationCompareService: ReservationCompareService) {
  }

  setActiveStatusCodes(statusCodes: Array<ReservationCompareStatusCode>): void {
    this.currentActiveStatusCodes.next(statusCodes);
  }

  getActiveStatusCodes(): Observable<Array<ReservationStatusCode>> {
    return this.currentActiveStatusCodes.asObservable().share();
  }

  getInstantActiveStatusCodes(): Array<ReservationCompareStatusCode> {
    return this.currentActiveStatusCodes.getValue();
  }

  getReservationList(): Observable<List<ReservationCompare>> {
    return this.currentReservationList.asObservable();
  }

  getVendorCompareList(eventPartyId: number): Observable<List<ReservationCompare>> {
    return this.currentReservationList.map((filteredList: List<ReservationCompare>) => {
      return filteredList.filter((filtered: ReservationCompare) => filtered.eventPartyId === eventPartyId);
    });
  }

  getShowReservationCompareSummary(): Observable<boolean> {
    return this.currentReservationSummaryState.asObservable();
  }

  setShowReservationCompareSummary(showSummary: boolean): void {
    return this.currentReservationSummaryState.next(showSummary);
  }

  getReservations(eventId: number): Observable<List<ReservationCompare>> {
    return this.reservationCompareService.getReservations(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.RESERVATION_COMPARE',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<ReservationCompare>) => {
        reservationList = reservationList.sort(ReservationUtils.reservationCompareComparator).toList();
        this.currentReservationList.next(reservationList);
        return reservationList;
      });
  }

  getReservationsByParty(eventId: number, eventPartyId: number): Observable<List<ReservationCompare>> {
    return this.reservationCompareService.getReservationsByParty(eventId, eventPartyId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.RESERVATION_COMPARE',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<ReservationCompare>) => {
        reservationList = reservationList.sort(ReservationUtils.reservationCompareComparator).toList();
        this.currentReservationList.next(reservationList);
        return reservationList;
      });
  }

  getFilteredReservations(eventId: number): Observable<List<ReservationCompare>> {
    return this.getActiveStatusCodes().flatMap((statusCodes: ReservationStatusCode[]) => {
      return this.getReservations(eventId).map(reservationList => {
        return this.getReservationListFilteredByStatus(reservationList, statusCodes);
      });
    });
  }

  getCurrentFilteredReservations(): Observable<List<ReservationCompare>> {
    return this.getActiveStatusCodes().flatMap((statusCodes: ReservationStatusCode[]) => {
      return this.getReservationList().map(reservationList => {
        return this.getReservationListFilteredByStatus(reservationList, statusCodes);
      });
    });
  }

  getVendorCurrentFilteredReservations(eventPartyId: number): Observable<List<ReservationCompare>> {
    return this.getActiveStatusCodes().flatMap((statusCodes: ReservationStatusCode[]) => {
      return this.getVendorCompareList(eventPartyId).map(reservationList => {
        return this.getReservationListFilteredByStatus(reservationList, statusCodes);
      });
    });
  }

  getReservationListFilteredByStatus(reservationList: List<ReservationCompare>,
                                     statusCodes: ReservationStatusCode[]): List<ReservationCompare> {
    return reservationList.filter((reservation: ReservationCompare) => {
      return statusCodes.find(statusCode => statusCode.toString() === reservation.status) != null;
    }).toList();
  }

  resetStatusCodes(): void {
    this.setActiveStatusCodes(ReservationCompareStore.STATUS_CODES);
  }

  resetReservationList(): void {
    this.setReservationList(List([]));
  }

  setReservationList(list: List<ReservationCompare>): void {
    this.currentReservationList.next(list);
  }

  isCompareListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.currentReservationList);
  }

  isVendorCompareListEmpty(eventPartyId: number): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getVendorCompareList(eventPartyId));
  }

  isFilteredMatchListEmpty(matchType: MatchTypeEnum): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getCurrentFilteredReservations().map((comparisonList: List<ReservationCompare>) => {
      return comparisonList.filter((comparison: ReservationCompare) => comparison.matchType === matchType.toString()).toList();
    }));
  }

  isVendorFilteredMatchListEmpty(matchType: MatchTypeEnum, eventPartyId): Observable<boolean> {
    return ReservationUtils
      .isListEmpty(this.getVendorCurrentFilteredReservations(eventPartyId).map((comparisonList: List<ReservationCompare>) => {
        return comparisonList.filter((comparison: ReservationCompare) => comparison.matchType === matchType.toString()).toList();
    }));
  }

  isMatchListEmpty(matchType: MatchTypeEnum): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getReservationList().map((comparisonList: List<ReservationCompare>) => {
      return comparisonList.filter((comparison: ReservationCompare) => comparison.matchType === matchType.toString()).toList();
    }));
  }

  areRsvpsPresent(): Observable<boolean> {
    return ReservationUtils.getNumberOfRsvps(this.getReservationList()).map(size => {
      return size > 0;
    });
  }

  areVendorRsvpsPresent(eventPartyId: number): Observable<boolean> {
    return ReservationUtils.getNumberOfRsvps(this.getVendorCompareList(eventPartyId)).map(size => {
      return size > 0;
    });
  }

  areVendorReservationPresent(eventPartyId: number): Observable<boolean> {
    return ReservationUtils.getNumberOfReservations(this.getVendorCompareList(eventPartyId)).map(size => {
      return size > 0;
    });
  }

  refreshCurrentReservationList(eventId: number) {
    this.reservationCompareService.getReservations(eventId)
      .pipe(
        take(1),
        catchError((error: any) => {
          this.flashMessagesService.error(
            'MESSAGES.ERROR.RESERVATION_COMPARE',
            `Error retrieving room details: ${error}`
          );
          return Observable.throw(error);
        }),
        map((reservationList: List<ReservationCompare>) => {
          return reservationList.sort(ReservationUtils.reservationCompareComparator).toList();
        })
      ).subscribe(reservationList => {
        this.setReservationList(reservationList);
      });
  }

  refreshCurrentReservationListByParty(eventId: number, eventPartyId: number) {
    this.reservationCompareService.getReservationsByParty(eventId, eventPartyId)
      .pipe(
        take(1),
        catchError((error: any) => {
          this.flashMessagesService.error(
            'MESSAGES.ERROR.RESERVATION_COMPARE',
            `Error retrieving room details: ${error}`
          );
          return Observable.throw(error);
        }),
        map((reservationList: List<ReservationCompare>) => {
          return reservationList.sort(ReservationUtils.reservationCompareComparator).toList();
        })
      ).subscribe(reservationList => {
        this.setReservationList(reservationList);
      });
  }

  getRsvps(eventId: number): Observable<List<ReservationCompare>> {
    return this.reservationCompareService.getRsvps(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.RESERVATION_COMPARE',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<ReservationCompare>) => {
        reservationList = reservationList.sort(ReservationUtils.rsvpCompareComparator).toList();
        this.currentReservationList.next(reservationList);
        return reservationList;
      });
  }

  getRsvpsByParty(eventId: number, eventPartyId: number): Observable<List<ReservationCompare>> {
    return this.reservationCompareService.getRsvpsByParty(eventId, eventPartyId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.RESERVATION_COMPARE',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<ReservationCompare>) => {
        reservationList = reservationList.sort(ReservationUtils.rsvpCompareComparator).toList();
        this.currentReservationList.next(reservationList);
        return reservationList;
      });
  }

  refreshCurrentRsvpList(eventId: number) {
    this.reservationCompareService.getRsvps(eventId)
      .pipe(
        take(1),
        catchError((error: any) => {
          this.flashMessagesService.error(
            'MESSAGES.ERROR.RESERVATION_COMPARE',
            `Error retrieving room details: ${error}`
          );
          return Observable.throw(error);
        }),
        map((rsvpList: List<ReservationCompare>) => {
          return rsvpList.sort(ReservationUtils.rsvpCompareComparator).toList();
        })
      ).subscribe(reservationList => {
      this.setReservationList(reservationList);
    });
  }

  refreshCurrentRsvpListByParty(eventId: number, eventPartyId: number) {
    this.reservationCompareService.getRsvpsByParty(eventId, eventPartyId)
      .pipe(
        take(1),
        catchError((error: any) => {
          this.flashMessagesService.error(
            'MESSAGES.ERROR.RESERVATION_COMPARE',
            `Error retrieving room details: ${error}`
          );
          return Observable.throw(error);
        }),
        map((rsvpList: List<ReservationCompare>) => {
          return rsvpList.sort(ReservationUtils.rsvpCompareComparator).toList();
        })
      ).subscribe(reservationList => {
      this.setReservationList(reservationList);
    });
  }

  getReservationsAndRsvps(eventId: number): Observable<List<ReservationCompare>> {
    return this.reservationCompareService.getReservationsAndRsvps(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.RESERVATION_COMPARE',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<ReservationCompare>) => {
        reservationList = reservationList.sort(ReservationUtils.rsvpCompareComparator).toList();
        this.currentReservationList.next(reservationList);
        return reservationList;
      });
  }

  getReservationsAndRsvpsByParty(eventId: number, eventPartyId: number): Observable<List<ReservationCompare>> {
    return this.reservationCompareService.getReservationsAndRsvpsByParty(eventId, eventPartyId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.RESERVATION_COMPARE',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<ReservationCompare>) => {
        reservationList = reservationList.sort(ReservationUtils.rsvpCompareComparator).toList();
        this.currentReservationList.next(reservationList);
        return reservationList;
      });
  }

  getHeaderSummary(recordType: SummaryRecordTypeEnum, matchType: MatchTypeEnum): Observable<StaySummary> {
    return this.getReservationList().flatMap(reservationList => {
      return this.getActiveStatusCodes().map((statusCodes: Array<ReservationStatusCode>) => {
        return this.getHeaderSummaryData(
          this.getReservationListFilteredByStatus(reservationList, statusCodes), recordType, matchType);
      });
    });
  }

  getVendorHeaderSummary(eventPartyId: number, recordType: SummaryRecordTypeEnum, matchType: MatchTypeEnum): Observable<StaySummary> {
    return this.getVendorCompareList(eventPartyId).flatMap(reservationList => {
      return this.getActiveStatusCodes().map((statusCodes: Array<ReservationStatusCode>) => {
        return this.getHeaderSummaryData(
          this.getReservationListFilteredByStatus(reservationList, statusCodes), recordType, matchType);
      });
    });
  }

  getHeaderSummaryData(dataList: List<ReservationCompare>, recordType: SummaryRecordTypeEnum, matchType: MatchTypeEnum): StaySummary {
    let totalNights = 0;
    let totalRevenue = 0;
    let avgRoomRate = 0;
    let totalItems = 0;
    let totalCommission = 0;

    let totalNightsReservations = 0;
    let totalRevenueReservations = 0;
    let totalItemsReservations = 0;
    let totalCommissionReservations = 0;
    let totalNightsRSVPs = 0;
    let totalRevenueRSVPs = 0;
    let totalItemsRSVPs = 0;
    let totalCommissionRSVPs = 0;

    if (recordType === SummaryRecordTypeEnum.RESERVATION) {
      totalNights = dataList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.roomNights))
        .filter(compare => OVERVIEW_STATUS_CODES.find(statusCode => statusCode.toString() === compare.reservation.status) != null)
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.reservation.roomNights)
        .reduce((prev: number, current: number) => prev + current, 0);
      totalRevenue = dataList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.revenue))
        .filter(compare => OVERVIEW_STATUS_CODES.find(statusCode => statusCode.toString() === compare.reservation.status) != null)
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.reservation.revenue)
        .reduce((prev: number, current: number) => prev + current, 0);
      totalItems = dataList
        .filter(compare => !isNullOrUndefined(compare.reservation))
        .filter(compare => OVERVIEW_STATUS_CODES.find(statusCode => statusCode.toString() === compare.reservation.status) != null)
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .count();
      totalCommission = dataList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.commission))
        .filter(compare => OVERVIEW_STATUS_CODES.find(statusCode => statusCode.toString() === compare.reservation.status) != null)
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.reservation.commission)
        .reduce((prev: number, current: number) => prev + current, 0);
    } else if (recordType === SummaryRecordTypeEnum.RSVP) {
      totalNights = dataList
        .filter(compare => !isNullOrUndefined(compare.rsvp) && !isNullOrUndefined(compare.rsvp.nights))
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.rsvp.nights)
        .reduce((prev: number, current: number) => prev + current, 0);
      totalRevenue = dataList
        .filter(compare => !isNullOrUndefined(compare.rsvp) && !isNullOrUndefined(compare.rsvp.revenue))
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.rsvp.revenue)
        .reduce((prev: number, current: number) => prev + current, 0);
      totalItems = dataList
        .filter(compare => !isNullOrUndefined(compare.rsvp))
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .count();
      totalCommission = dataList
        .filter(compare => !isNullOrUndefined(compare.rsvp) && !isNullOrUndefined(compare.rsvp.commission))
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.rsvp.commission)
        .reduce((prev: number, current: number) => prev + current, 0);
    } else if (recordType === SummaryRecordTypeEnum.RESERVATION_RSVP) {
      totalNightsReservations = dataList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.roomNights))
        .filter(compare => OVERVIEW_STATUS_CODES.find(statusCode => statusCode.toString() === compare.reservation.status) != null)
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.reservation.roomNights)
        .reduce((prev: number, current: number) => prev + current, 0);
      totalRevenueReservations = dataList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.revenue))
        .filter(compare => OVERVIEW_STATUS_CODES.find(statusCode => statusCode.toString() === compare.reservation.status) != null)
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.reservation.revenue)
        .reduce((prev: number, current: number) => prev + current, 0);
      totalItemsReservations = dataList
        .filter(compare => !isNullOrUndefined(compare.reservation))
        .filter(compare => OVERVIEW_STATUS_CODES.find(statusCode => statusCode.toString() === compare.reservation.status) != null)
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .count();
      totalCommissionReservations = dataList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.commission))
        .filter(compare => OVERVIEW_STATUS_CODES.find(statusCode => statusCode.toString() === compare.reservation.status) != null)
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .map(compare => compare.reservation.commission)
        .reduce((prev: number, current: number) => prev + current, 0);

      totalNightsRSVPs = dataList
        .filter(compare => !isNullOrUndefined(compare.rsvp) && !isNullOrUndefined(compare.rsvp.nights))
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .filter(compare => !compare.subscriber)
        .map(compare => compare.rsvp.nights)
        .reduce((prev: number, current: number) => prev + current, 0);
      totalRevenueRSVPs = dataList
        .filter(compare => !isNullOrUndefined(compare.rsvp) && !isNullOrUndefined(compare.rsvp.revenue))
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .filter(compare => !compare.subscriber)
        .map(compare => compare.rsvp.revenue)
        .reduce((prev: number, current: number) => prev + current, 0);
      totalItemsRSVPs = dataList
        .filter(compare => !isNullOrUndefined(compare.rsvp))
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .filter(compare => !compare.subscriber)
        .count();
      totalCommissionRSVPs = dataList
        .filter(compare => !isNullOrUndefined(compare.rsvp) && !isNullOrUndefined(compare.rsvp.commission))
        .filter(compare => matchType && compare.matchType === matchType.toString() || !matchType)
        .filter(compare => !compare.subscriber)
        .map(compare => compare.rsvp.commission)
        .reduce((prev: number, current: number) => prev + current, 0);

      totalNights = totalNightsReservations + totalNightsRSVPs;
      totalRevenue = totalRevenueReservations + totalRevenueRSVPs;
      totalItems = totalItemsReservations + totalItemsRSVPs;
      totalCommission = totalCommissionReservations + totalCommissionRSVPs;
    }


    if (totalNights > 0) {
      avgRoomRate = totalRevenue / totalNights;
    }

    // Generate a room summary
    const staySummary = new StaySummary({
      totalNights: totalNights,
      totalCommission: totalCommission,
      totalRevenue: totalRevenue,
      avgRoomRate: avgRoomRate,
      totalItems: totalItems
    });

    return staySummary;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/reservation-compare-store.ts